import { WPElement, useEffect, useState, useRef, useLayoutEffect, forwardRef, useMemo } from "@wordpress/element";
import { Popover, Transition } from '@headlessui/react'
import { buildButton } from "./button";
import { useFloating, offset, shift, limitShift, autoPlacement, inline, detectOverflow } from '@floating-ui/react-dom';
import { IconButton } from './icon-button';
import { CloseIcon } from '../icons';
import cx from "classnames";

export function Dropdown( props: {
	button: Function,
	children: WPElement | WPElement[],
	className?: string,
	open?: boolean,
	close?: Function,
	placement?: "bottom" | "flyout" | Record< string, any >
} ) {
	let [ referenceElement, setReferenceElement ] = useState<any>();

	const cl = "overflow-hidden shadow-lg rounded-2xl ring-1 ring-black ring-opacity-5 drop-shadow-xl";

	const attr = {};

	if ( props.open !== undefined ) {
		attr[ "open" ] = props.open;
	}

	if ( props.close !== undefined ) {
		//attr["close"] = props.close;
	}

	return (
		<Popover className="inline w-full md:w-auto shrink-0">
			<>
				{ props.button( { setReferenceElement } ) }
				<Popover.Panel className={ `z-20 ${ props.className || "" }` }>
					{ ( { open, close } ) => (
						<PopperWrapper placement={ props.placement } open={ open } close={ close } className={ cl } referenceElement={ referenceElement }>{ props.children }</PopperWrapper>
					) }
				</Popover.Panel>
			</>
		</Popover>
	)
}

Dropdown.Button = Popover.Button;

Dropdown.Panel = function ( props: { children: any, className?: string } ) {
	return (
		<div className={ cx( "h-full overflow-y-auto bg-white text-ui-700", props.className ) }>
			{ props.children }
		</div>
	)
}

export function DropdownButton( props: {
	as?: "button" | string,
	label: string | Function,
	variant?: any,
	leftIcon?: any,
	rightIcon?: any,
	className?: string,
	size?: string,
	children: WPElement | WPElement[],
	placement?: "bottom" | "flyout" | Record< string, any >
} ) {
	const createButton = ( disableFocus?: boolean ) => {
		return buildButton( {
			variant: props.variant,
			leftIcon: props.leftIcon,
			rightIcon: props.rightIcon,
			size: props.size,
			disableFocus: disableFocus
		} );
	};

	const def = {
		elementType: !props.as || props.as == "button" ? "button" : props.as,
		button: null,
		className: ""
	};

	if ( def.elementType == "button" ) {
		def.button = createButton();
	}

	const [ state, setState ] = useState<any>( def );

	useEffect( () => {
		state.elementType = ! props.as || props.as == "button" ? "button" : props.as;

		if ( state.elementType == "button" ) {
			state.button = createButton();
			state.className = state.button.className;
		}

		setState( { ...state } );
	}, [ props.as, props.label ] );

	const label = ( o: any ) => {
		if ( state.button == null ) {
			return props.label( o );
		} else {
			if ( typeof ( props.label ) == "string" ) {
				return state.button.render( props.label );
			} else {
				return state.button.render( props.label( o ) );
			}
		}
	};

	const click = () => {
		// focus fix, deaktiviert den focus wenn die Maus benutzt wurde
		if ( state.elementType == "button" ) {
			state.button = createButton( true );
			state.className = state.button.className;
			setState( { ...state } );
		}
	};

	return (
		<Dropdown placement={ props.placement } className="relative" button={ ( { setReferenceElement } ) => (
			<Popover.Button onClick={ click } as={ state.elementType } className={ state.className } ref={ setReferenceElement }>
				{ ( o: any ) => (
					label( o )
				) }
			</Popover.Button>
		) }>
			{ props.children }
		</Dropdown>
	)
}

DropdownButton.Panel = Dropdown.Panel;

DropdownButton.ClosePanel = ( props: { className?: string, close: Function, children: any } ) => {
	return (
		<DropdownButton.Panel className={ props.className }>
			<div className='pb-4 lg:pb-8'>
				<div className='flex flex-col items-end w-full pr-2 mt-2'>
					<div className={ cx(
						'focus:outline-none no-tap-highlight',
						'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:rounded-full focus-visible:ring-ui-primary'
					) }>
						<IconButton onClick={ () => props.close() } variant={ 'transparent' } size={ 'xs' } icon={ <CloseIcon /> }></IconButton>
					</div>
				</div>
				<div className='px-8 mt-2'>
					{ props.children }
				</div>
			</div>
		</DropdownButton.Panel>
	)
}

function flyoutMiddleware( opts: { offset?: number } ) {
	return {
		name: 'flyoutMiddleware',
		async fn( middlewareArguments ) {
			const { platform, elements, strategy, rects } = middlewareArguments;

			const clippingClientRect = await platform.getClippingRect( {
				element: ( await platform.getDocumentElement?.( elements.floating ) ),
				boundary: "clippingAncestors",
				rootBoundary: "viewport",
				strategy: strategy,
			} );

			let x = rects.reference.x + ( rects.reference.width / 2 );
			x = x - rects.floating.width / 2;

			let y = rects.reference.y + rects.reference.height;
			if ( opts && opts.offset ) {
				y += opts.offset;
			}

			let width = x + rects.floating.width;
			if ( width > clippingClientRect.width ) {
				x = x - ( width - clippingClientRect.width ) - 8;
			}

			let height = y + rects.floating.height;
			if ( height > clippingClientRect.height ) {
				y = 0;
			}

			return {
				x: x,
				y: y
			};
		}
	}
}

function PopperWrapper( props: {
	open: boolean,
	close: any,
	className: string,
	referenceElement: any,
	children: WPElement | WPElement[],
	placement?: "bottom" | "flyout" | Record< string, any >
} ) {
	const middleware = useMemo( () => {
		if ( ! props.placement || props.placement == 'bottom' ) {
			return shift( {
				padding: 4,
				limiter: limitShift()
			} );
		} else {
			return flyoutMiddleware( {
				offset: props.placement != 'flyout' && 'offset' in props.placement ? props.placement.offset : 12
			} );
		}

	}, [ props.placement ] );

	const { x, y, reference, floating, strategy, update } = useFloating( {
		placement: "bottom",
		middleware: [
			offset( 4 ),
			middleware
		]
	} );

	useLayoutEffect( () => {
		if ( props.open ) {
			reference( props.referenceElement );
		}
	}, [ reference, props.referenceElement, props.open ] );

	return (
		<>
			<div
				className={ props.className }
				style={ {
					position: strategy,
					top: y ?? 0,
					left: x ?? 0,
					width: 'max-content',
				} }
			>
				<Content ref={ floating } open={ props.open } close={ props.close }>{ props.children }</Content>
			</div>
		</>
	)
}

const Content = forwardRef( ( props: { open: boolean, close: Function, children: any }, ref: any ) => {
	return (
		<div ref={ ref }>
			{ typeof ( props.children ) == "function"
				? props.children( { open: props.open, close: props.close } )
				: props.children
			}
			<div className="p-1 bg-primary-500 rounded-b-md"></div>
		</div>
	)
} );
